import { useState } from "react";
import { toast } from "react-hot-toast";
import { CreditCard, Download, Zap, Receipt, Sparkles, Check, Layers, Plus, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ProgressBar } from "./ProgressBar";

export function BillingSection() {
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [invoices, setInvoices] = useState<Array<{ id: string; date: string; amount: string; status: string }>>([]);

  // Default workspace starter tier
  const plan = {
    name: "Starter Workspace",
    priceLabel: "Free Beta",
    billingCycle: "Included with early access",
    features: [
      "Up to 3 connected messaging channels (Messenger, Instagram, WhatsApp)",
      "1 commerce store sync (Shopify or WooCommerce)",
      "Full AI customer support automation",
      "Omnichannel inbox and lead management",
    ],
  };

  const usage = {
    messagesUsed: 0,
    messageLimit: 2500,
  };
  const usagePct = usage.messageLimit > 0 ? usage.messagesUsed / usage.messageLimit : 0;

  return (
    <div className="space-y-5">
      {/* Current Plan Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Subscription Plan &amp; Tier
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Active Plan
          </span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="text-base font-bold text-slate-900 dark:text-slate-100">{plan.name}</span>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                {plan.priceLabel} &middot; {plan.billingCycle}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" className="h-9 px-4 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 gap-1.5" onClick={() => toast("Plan comparison coming soon")}>
                <Layers className="h-3.5 w-3.5" />
                Compare plans
              </Button>
              <Button type="button" className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs gap-1.5" onClick={() => toast("Upgrade flow coming soon")}>
                <Sparkles className="h-3.5 w-3.5" />
                Upgrade plan
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            {plan.features.map((feature) => (
              <div key={feature} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 shrink-0">
                  <Check className="h-2.5 w-2.5" />
                </span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Usage Card */}
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400">
              <Zap className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Monthly Usage Quota
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Resets on 1st of month</span>
        </div>

        <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {usage.messagesUsed.toLocaleString()} / {usage.messageLimit.toLocaleString()} messages handled
            </span>
            <span className="font-bold text-slate-900 dark:text-slate-100">{Math.round(usagePct * 100)}%</span>
          </div>
          <ProgressBar
            value={usage.messagesUsed}
            max={usage.messageLimit}
            fillClassName={usagePct >= 0.9 ? "bg-amber-500" : "bg-primary"}
          />
        </div>
      </Card>

      {/* Payment & Invoices */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
          <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                <CreditCard className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                Payment Method
              </span>
            </div>
          </div>
          <div className="p-3.5 bg-white/70 dark:bg-slate-900/70">
            {hasPaymentMethod ? (
              <div className="flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Visa &bull;&bull;&bull;&bull; 4242</p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Expires 09/27</p>
                  </div>
                </div>
                <Button type="button" variant="outline" className="h-8.5 px-3.5 text-xs font-semibold rounded-xl shadow-xs border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => toast("Card update coming soon")}>
                  Update
                </Button>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850/30 p-3 text-center space-y-2">
                <p className="text-xs text-slate-500 dark:text-slate-400">No payment method attached to this workspace.</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs font-semibold rounded-xl gap-1"
                  onClick={() => toast("Payment gateway connection coming soon")}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add payment method
                </Button>
              </div>
            )}
          </div>
        </Card>

        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
          <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <Receipt className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                Recent Invoices
              </span>
            </div>
          </div>
          <div className="p-3.5 bg-white/70 dark:bg-slate-900/70 space-y-1.5">
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-2.5 py-1.5 shadow-2xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{invoice.date}</span>
                    <span className="inline-flex items-center rounded-md border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.2 text-[8px] font-bold text-emerald-700 dark:text-emerald-400">
                      {invoice.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100 tabular-nums">{invoice.amount}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                      aria-label={`Download invoice for ${invoice.date}`}
                      onClick={() => toast.success("Invoice download started")}
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850/30 p-3 text-center">
                <p className="text-xs text-slate-500 dark:text-slate-400">No invoices yet. Your billing receipts will appear here.</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default BillingSection;
